import React, { useEffect } from 'react';
import { Box, Avatar, Typography, Badge } from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import { getSocket } from "../../../socket/socket.js";
import { setReadCount, clearSelectedGroup } from '../../../slice/uiSlice.jsx';
import OnlineIndicator from "../OnlineIndicator.jsx";

const UserListItem = ({ listUser, isSelected, onSelectUser }) => {
  const dispatch = useDispatch();
  const socket = getSocket();
  const LoginUserId = useSelector((state) => state.auth.user);
  const unreadCount = useSelector((state) => state.ui.chatInfo.unreadCounts[listUser._id] || 0);
  const latestMessage = useSelector((state) => state.ui.chatInfo.latestMessage[listUser._id]);

  useEffect(() => {
    if (!socket) return;
    socket.emit('getunReadCountUpdate', {
      sender: listUser._id,
      receiver: LoginUserId.id
    });

    const handleUnreadCountUpdate = (data) => {
      if (data.receiver === LoginUserId.id && typeof data.unreadCount === 'number' && data.sender === listUser._id) {
        dispatch(setReadCount({
          value: data.unreadCount,
          sender: data.sender,
          receiver: data.receiver,
          latestMessage: data.latestMessage
        }));
      }
    };

    socket.on('unreadCountUpdate', handleUnreadCountUpdate);
    return () => socket.off('unreadCountUpdate', handleUnreadCountUpdate);
  }, [socket, LoginUserId.id, listUser._id, dispatch]);

  return (
    <Box
      onClick={() => {
        onSelectUser(listUser);
        dispatch(clearSelectedGroup());
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        padding: '10px 14px',
        cursor: 'pointer',
        gap: '12px',
        position: 'relative',
        transition: 'all 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
        background: isSelected ? 'rgba(42, 120, 209, 0.08)' : 'transparent',
        borderRadius: '12px',
        mx: '6px',
        mb: '2px',
        border: isSelected ? '1px solid rgba(42, 120, 209, 0.15)' : '1px solid transparent',
        '&:hover': { 
          background: isSelected ? 'rgba(42, 120, 209, 0.12)' : '#F8FAFC',
          transform: isSelected ? 'none' : 'translateX(4px)'
        }
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <Avatar 
          src={listUser.profileImage || ""}
          sx={{ 
            width: 36, 
            height: 36, 
            fontSize: '13px', 
            fontWeight: 700, 
            bgcolor: isSelected ? '#2a78d1' : '#E2E8F0',
            color: isSelected ? '#FFFFFF' : '#475569',
            boxShadow: isSelected ? '0 4px 12px rgba(42, 120, 209, 0.25)' : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          {listUser.name?.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ position: 'absolute', bottom: -1, right: -1 }}>
          <OnlineIndicator userId={listUser._id} size="small" />
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography 
            sx={{ 
              fontSize: '13px', 
              fontWeight: isSelected ? 700 : 600, 
              color: isSelected ? '#1E293B' : '#334155',
              fontFamily: "'Inter', sans-serif",
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {listUser.name}
          </Typography>
          {unreadCount > 0 && (
            <Badge 
              badgeContent={unreadCount} 
              sx={{ 
                '& .MuiBadge-badge': { 
                  backgroundColor: '#2a78d1', 
                  color: '#FFFFFF', 
                  fontSize: '9px',
                  fontWeight: 800,
                  height: '18px',
                  minWidth: '18px',
                  boxShadow: '0 2px 6px rgba(42, 120, 209, 0.3)'
                } 
              }} 
            />
          )}
        </Box>
        <Typography 
          sx={{ 
            fontSize: '11px', 
            color: isSelected ? '#64748B' : '#94A3B8', 
            fontFamily: "'Inter', sans-serif",
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            marginTop: '2px',
            fontWeight: unreadCount > 0 ? 600 : 400
          }}
        >
          {latestMessage?.content ?? (listUser.status || "No messages yet")}
        </Typography>
      </Box>
    </Box>
  );
};

export default UserListItem;

